// Build a scene resolver for the live OGS-streaming client from a character's
// server-side OGS bundle rows (GET /api/characters/{slug}/ogs — the same loose-file
// collection the on-device OGS render path mounts).
//
// Each runtime file is its own row served at `/api/characters/{slug}/ogs/{filename}`,
// so rows do NOT share a directory prefix — resolution is by basename. This turns
// the row list into the { sceneUrl, resolveAssetUrl } pair `loadStreamScene` needs,
// plus the rig-gather URL for lip-sync, keyed by each file's basename (scene.json,
// mesh.bin, rig_gather.json, …).
//
// Pure + dependency-light (only the URL normalizer) so it is unit-testable with
// no network. No silent fallback / fake data: an empty list, a missing scene
// row, or a referenced file with no row all throw (CLAUDE.md loud-failure rule)
// — the caller surfaces "no server bundle" instead of quietly using stale data.

import { toProxiedAssetUrl } from "../../lib/characterAssetUrl";
import type { AssetUrlResolver } from "./streamScene";

export interface OGSAssetRow {
  /** Optional asset-class tag — the scene descriptor row may be `kind: "scene"`. */
  kind?: string;
  name?: string;
  /** /ogs rows carry the basename here; scene/gather are matched on it. */
  filename?: string;
  /** AAM ships both casings; either is accepted. */
  file_url?: string;
  fileUrl?: string;
  /** Byte size of the stored file (AAM ships both casings). */
  size?: number;
  byte_size?: number;
  byteSize?: number;
}

export interface OGSSceneResolver {
  /** AAM-relative path of the scene.json descriptor row. */
  sceneUrl: string;
  /** Map a filename referenced by scene.json (e.g. "mesh.bin") → its AAM path. */
  resolveAssetUrl: AssetUrlResolver;
  /** AAM-relative path of the per-character rig-gather row (ARKit→server-rig index
   *  map for lip-sync), or null when the character has no gather (→ neutral face). */
  gatherUrl: string | null;
  /** Row byte size by filename, for the asset cache's size-versioned key
   *  (a re-upload changes the size → cache MISS → refetch). Undefined when the
   *  row carried no size. */
  sizeOf: (filename: string) => number | undefined;
}

function rawUrl(row: OGSAssetRow): string {
  return row.fileUrl ?? row.file_url ?? "";
}

function basename(url: string): string {
  const noQuery = url.split("?")[0];
  const i = noQuery.lastIndexOf("/");
  return i >= 0 ? noQuery.slice(i + 1) : noQuery;
}

/** Turn a character's /ogs row list into the scene resolver the streaming client
 *  lifts against. Throws loudly on an empty / incomplete bundle.
 *
 *  Paths are returned as the AAM serves them — relative, and NOT prefixed with the
 *  asset-manager base. The caller fetches them through `assetManagerApi`
 *  (fetchAamFile / cachedFetchAamFile), which owns the single URL policy for every
 *  /ogs consumer. Prefixing the base here instead is what produced a bare
 *  `/api/characters/{slug}/ogs/{file}` that neither the Vite proxy nor nginx routes
 *  to the AAM (both only special-case the `*-assets-file` paths), so it fell through
 *  to convorcher and 404'd.
 *
 *  A row whose URL is an absolute S3/MinIO link is still normalised by
 *  `toProxiedAssetUrl` to the credentialed `/api/character-assets/...` path — that
 *  bucket 403s when fetched directly. */
export function buildOGSSceneResolver(
  rows: OGSAssetRow[] | null | undefined,
): OGSSceneResolver {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(
      "ogs assets: empty list — this character has no server-side streaming bundle",
    );
  }

  const byName = new Map<string, string>();
  const sizeByName = new Map<string, number>();
  let sceneUrl = "";
  let gatherUrl: string | null = null;
  for (const row of rows) {
    const raw = rawUrl(row);
    if (!raw) continue;
    const proxied = toProxiedAssetUrl(raw);
    const fname = basename(raw);
    byName.set(fname, proxied);
    // Same spellings the server accepts (server/asset_fetch.py). Missing the one the
    // AAM actually ships silently drops size-versioning from the cache key, which is
    // the stale-shadow failure that key exists to prevent.
    const size = row.size ?? row.byteSize ?? row.byte_size;
    if (typeof size === "number") sizeByName.set(fname, size);
    if (row.kind === "scene" || fname === "scene.json") sceneUrl = proxied;
    if (row.kind === "rig_gather" || fname === "rig_gather.json") gatherUrl = proxied;
  }

  if (!sceneUrl) {
    throw new Error(
      'ogs assets: no scene descriptor (a row with kind "scene" or filename scene.json)',
    );
  }

  return {
    sceneUrl,
    gatherUrl,
    sizeOf(filename: string): number | undefined {
      return sizeByName.get(basename(filename));
    },
    resolveAssetUrl(filename: string): string {
      const u = byName.get(basename(filename));
      if (!u) {
        throw new Error(
          `ogs assets: scene.json references "${filename}" but no matching row exists (server bundle incomplete)`,
        );
      }
      return u;
    },
  };
}
